import { useCallback, useState } from 'react';

const HISTORY_LIMIT = 50;

const cloneGraphSnapshot = (graphState) => JSON.parse(JSON.stringify(graphState));

/**
 * Manages undo/redo stacks and wraps reducer actions with optimistic validation.
 */
export const useGraphHistory = ({ graphStateRef, dispatchGraph, reduceGraphAction, showFeedback, graphActions }) => {
  const [historyPast, setHistoryPast] = useState([]);
  const [historyFuture, setHistoryFuture] = useState([]);

  const applyGraphAction = useCallback(
    (action, options = {}) => {
      const { recordHistory = true } = options;
      const previousState = graphStateRef.current;
      const preview = reduceGraphAction(previousState, action);

      if (!preview.valid) {
        dispatchGraph(action);
        if (preview.nextState?.lastError) {
          showFeedback(preview.nextState.lastError, 'error');
        }
        return false;
      }

      if (recordHistory) {
        const snapshot = cloneGraphSnapshot(previousState);
        setHistoryPast((prev) => [...prev.slice(-(HISTORY_LIMIT - 1)), snapshot]);
        setHistoryFuture([]);
      }

      dispatchGraph(action);
      return true;
    },
    [dispatchGraph, graphStateRef, reduceGraphAction, showFeedback],
  );

  const undo = useCallback(() => {
    setHistoryPast((past) => {
      if (past.length === 0) return past;

      const previous = past[past.length - 1];
      const currentSnapshot = cloneGraphSnapshot(graphStateRef.current);

      setHistoryFuture((future) => [...future, currentSnapshot]);
      dispatchGraph({
        type: graphActions.SET_GRAPH,
        payload: previous,
      });

      return past.slice(0, -1);
    });
  }, [dispatchGraph, graphActions.SET_GRAPH, graphStateRef]);

  const redo = useCallback(() => {
    setHistoryFuture((future) => {
      if (future.length === 0) return future;

      const next = future[future.length - 1];
      const currentSnapshot = cloneGraphSnapshot(graphStateRef.current);

      setHistoryPast((past) => [...past.slice(-(HISTORY_LIMIT - 1)), currentSnapshot]);
      dispatchGraph({
        type: graphActions.SET_GRAPH,
        payload: next,
      });

      return future.slice(0, -1);
    });
  }, [dispatchGraph, graphActions.SET_GRAPH, graphStateRef]);

  return {
    historyPast,
    historyFuture,
    canUndo: historyPast.length > 0,
    canRedo: historyFuture.length > 0,
    applyGraphAction,
    undo,
    redo,
  };
};

export default useGraphHistory;
